/**
 * MaterialUI Controller Context
 *
 * Global UI state: sidenav, theme mode, layout settings.
 * Pattern: useReducer + createContext.
 * Persistence: localStorage sync on state changes.
 */

import { createContext, useContext, useReducer, useMemo, useEffect } from "react";

const MaterialUI = createContext(null);
MaterialUI.displayName = "MaterialUIContext";

// localStorage key
const STORAGE_KEY = "kadmin_ui_settings";

// Helper to load from localStorage
const loadFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn("Failed to load UI settings from localStorage:", e);
  }
  return null;
};

// Define defaults
const defaultState = {
  miniSidenav: false,
  transparentSidenav: false,
  whiteSidenav: false,
  sidenavColor: "primary",
  transparentNavbar: true,
  fixedNavbar: true,
  openConfigurator: false,
  direction: "ltr",
  layout: "/admin/dashboard",
  darkMode: true,
};

// Merge saved state with defaults (saved takes precedence)
const savedState = loadFromStorage();
const initialState = savedState ? { ...defaultState, ...savedState } : defaultState;

function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV":
      return { ...state, transparentSidenav: action.value };
    case "WHITE_SIDENAV":
      return { ...state, whiteSidenav: action.value };
    case "SIDENAV_COLOR":
      return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR":
      return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR":
      return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR":
      return { ...state, openConfigurator: action.value };
    case "DIRECTION":
      return { ...state, direction: action.value };
    case "LAYOUT":
      return { ...state, layout: action.value };
    case "DARKMODE":
      return { ...state, darkMode: action.value };
    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
}

function MaterialUIControllerProvider({ children }) {
  const [controller, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [controller, dispatch], [controller]);
  
  // Persist settings to localStorage when they change
  useEffect(() => {
    try {
      // Only save persistent settings (exclude transient ones like openConfigurator)
      const toSave = {
        miniSidenav: controller.miniSidenav,
        transparentSidenav: controller.transparentSidenav,
        whiteSidenav: controller.whiteSidenav,
        sidenavColor: controller.sidenavColor,
        transparentNavbar: controller.transparentNavbar,
        fixedNavbar: controller.fixedNavbar,
        direction: controller.direction,
        darkMode: controller.darkMode,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
      console.warn("Failed to save UI settings to localStorage:", e);
    }
  }, [controller]);
  
  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

function useMaterialUIController() {
  const context = useContext(MaterialUI);
  if (!context) {
    throw new Error("useMaterialUIController must be used within MaterialUIControllerProvider");
  }
  return context;
}

const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
